import systemUnlocksJson from "./generated/systemUnlocks.json";
import { globalRepo, K } from "./globalRepo.js";

export type SystemUnlockCategory = "tab" | "system" | "subsystem";

export interface SystemUnlockDefinition {
  readonly id: string;
  readonly category: SystemUnlockCategory;
  readonly targetId: string;
  readonly phase: string;
  readonly unlockWorld: number;
  readonly unlockChapter: number;
  readonly unlockBestChapter: number;
  readonly unlockRebirthCount: number;
  readonly enabled: boolean;
  readonly titleKey: string;
  readonly descKey: string;
  readonly comment?: string;
}

interface SystemUnlockRow {
  readonly id: string;
  readonly category: string;
  readonly targetId: string;
  readonly phase: string;
  readonly unlockWorld: number;
  readonly unlockChapter: number;
  readonly unlockBestChapter: number;
  readonly unlockRebirthCount: number;
  readonly enabled: boolean;
  readonly titleKey: string;
  readonly descKey: string;
  readonly comment?: string;
}

function requireNonEmpty(value: string, field: string, id: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`systemUnlocks.tsv: ${id}.${field} is empty`);
  }
  return value;
}

function requirePositiveInteger(value: number, field: string, id: string): number {
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`systemUnlocks.tsv: ${id}.${field} must be a positive integer`);
  }
  return value;
}

function requireNonNegativeInteger(value: number, field: string, id: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`systemUnlocks.tsv: ${id}.${field} must be a non-negative integer`);
  }
  return value;
}

function requireCategory(value: string, id: string): SystemUnlockCategory {
  if (value === "tab" || value === "system" || value === "subsystem") return value;
  throw new Error(`systemUnlocks.tsv: ${id}.category must be tab, system, or subsystem`);
}

function worldChapterToBestChapter(worldNo: number, chapter: number): number {
  const span = Math.max(1, globalRepo.getInt(K.worldChapterSpan));
  return (worldNo - 1) * span + chapter;
}

function toDefinition(row: SystemUnlockRow): SystemUnlockDefinition {
  const id = requireNonEmpty(row.id, "id", row.id);
  const unlockWorld = requirePositiveInteger(row.unlockWorld, "unlockWorld", id);
  const unlockChapter = requirePositiveInteger(row.unlockChapter, "unlockChapter", id);
  const explicitBestChapter = requireNonNegativeInteger(row.unlockBestChapter, "unlockBestChapter", id);
  return {
    id,
    category: requireCategory(requireNonEmpty(row.category, "category", id), id),
    targetId: requireNonEmpty(row.targetId, "targetId", id),
    phase: requireNonEmpty(row.phase, "phase", id),
    unlockWorld,
    unlockChapter,
    unlockBestChapter: explicitBestChapter > 0 ? explicitBestChapter : worldChapterToBestChapter(unlockWorld, unlockChapter),
    unlockRebirthCount: requireNonNegativeInteger(row.unlockRebirthCount, "unlockRebirthCount", id),
    enabled: Boolean(row.enabled),
    titleKey: requireNonEmpty(row.titleKey, "titleKey", id),
    descKey: requireNonEmpty(row.descKey, "descKey", id),
    comment: typeof row.comment === "string" ? row.comment : "",
  };
}

function buildDefinitions(rows: readonly SystemUnlockRow[]): readonly SystemUnlockDefinition[] {
  const definitions = rows.map(toDefinition);
  const seen = new Set<string>();
  const duplicates: string[] = [];
  for (const def of definitions) {
    const key = `${def.category}:${def.targetId}`;
    if (seen.has(key)) duplicates.push(key);
    seen.add(key);
  }
  if (duplicates.length > 0) {
    throw new Error(`systemUnlocks.tsv: duplicate category/targetId ${duplicates.join(", ")}`);
  }
  return definitions;
}

export const SYSTEM_UNLOCK_DEFS = buildDefinitions(systemUnlocksJson as readonly SystemUnlockRow[]);

export function systemUnlockFor(
  category: SystemUnlockCategory,
  targetId: string,
): SystemUnlockDefinition | undefined {
  return SYSTEM_UNLOCK_DEFS.find((def) => def.enabled && def.category === category && def.targetId === targetId);
}

export function requiredBestChapterForSystem(
  category: SystemUnlockCategory,
  targetId: string,
  fallback = 1,
): number {
  return systemUnlockFor(category, targetId)?.unlockBestChapter ?? fallback;
}

export function isSystemUnlocked(
  category: SystemUnlockCategory,
  targetId: string,
  bestChapter: number,
  rebirthCount = 0,
): boolean {
  const def = systemUnlockFor(category, targetId);
  if (!def) return true;
  return bestChapter >= def.unlockBestChapter && rebirthCount >= def.unlockRebirthCount;
}

export function tabRequiredBestChapter(tabId: string): number {
  return requiredBestChapterForSystem("tab", tabId, 1);
}

export function systemRequiredBestChapter(targetId: string): number {
  return requiredBestChapterForSystem("system", targetId, 1);
}

export function subsystemRequiredBestChapter(targetId: string): number {
  return requiredBestChapterForSystem("subsystem", targetId, 1);
}
